import {useId} from 'react'
import {clsx} from 'clsx'

import {TriangleRightIcon} from '@primer/octicons-react'

import {NavGroup, type NavGroupType} from '../NavGroup/NavGroup'
import {NavLink} from '../NavLink/NavLink'
import type {NavLinkType} from '../NavLink/NavLink'

import {useDropdownDisclosure} from './use-dropdown-disclosure'
import {useDropdownPositioning} from './use-dropdown-positioning'

import styles from './NavDropdown.module.css'
import redesignStyles from '../../MarketingNavigationRedesign.module.css'

export type NavDropdownType = {
  groups: Array<NavGroupType & {featured?: boolean}>
  render?: boolean
  title: string
  trailingLink?: NavLinkType
}

type NavDropdownProps = NavDropdownType & {
  redesign?: boolean
  isOpen?: boolean
  onOpenChange?: (nextOpen: boolean) => void
}

export function NavDropdown(props: NavDropdownProps) {
  const {title, groups, render = true, trailingLink, isOpen = false, onOpenChange, redesign = false} = props
  const visibleGroups = groups.filter(group => group.render !== false)
  const featuredFirst = visibleGroups[0]?.featured

  const panelId = useId()

  const {containerRef, dropdownRef, buttonRef, containerHandlers, onButtonClick} = useDropdownDisclosure({
    isOpen,
    onOpenChange,
  })

  useDropdownPositioning(containerRef, dropdownRef, isOpen, !redesign)

  if (render === false) {
    return null
  }

  return (
    // Container handlers only add dismissal affordances (Escape, focus-out, mouse-leave); the button
    // is the interactive control, so the wrapper intentionally has no role.
    <div
      ref={containerRef}
      className={clsx(styles.container, redesign && redesignStyles.Container, {
        // Global `open` class drives the CSS open state, derived from React state.
        // eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals
        open: isOpen,
      })}
      {...containerHandlers}
    >
      <button
        ref={buttonRef}
        type="button"
        className={clsx(styles.button, redesign && redesignStyles.Button)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onButtonClick}
      >
        {title}

        <TriangleRightIcon size={16} className={clsx(styles.buttonIcon, redesign && redesignStyles.ButtonIcon)} />
      </button>

      <div ref={dropdownRef} id={panelId} className={clsx(styles.dropdown, redesign && redesignStyles.Dropdown)}>
        <div
          className={redesign ? clsx(redesignStyles.Panel, featuredFirst && redesignStyles.featuredFirst) : undefined}
        >
          <ul className={clsx(styles.list, redesign && redesignStyles.Groups)}>
            {visibleGroups.map(group => (
              <li
                key={group.title}
                className={
                  redesign
                    ? clsx(
                        redesignStyles.GroupCell,
                        group.featured && redesignStyles.featured,
                        group.hasSeparator && redesignStyles.separated,
                      )
                    : undefined
                }
              >
                <NavGroup {...group} context={title} redesign={redesign} />
              </li>
            ))}
          </ul>

          {trailingLink ? (
            <div className={clsx(styles.trailingLinkContainer, redesign && redesignStyles.Footer)}>
              <NavLink {...trailingLink} context={title} withArrow redesign={redesign} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
